"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { usePathname } from "@/i18n/navigation";
interface UseHideOnScrollOptions {
  /**
   * Selector for the page header banner. Auto-hide only applies on pages that
   * render a banner of meaningful height; the navbar hides once scrolled past it.
   */
  bannerSelector?: string;
  /**
   * Minimum banner height for auto-hide to apply. Pages without a real header
   * banner (e.g. search) render an empty/short wrapper and keep the navbar shown.
   */
  minBannerHeight?: number;
  /** Ignore scroll movements smaller than this (rubber-banding, momentum jitter). */
  threshold?: number;
  /** Force the navbar visible — e.g. while a menu or search overlay is open. */
  disabled?: boolean;
}

/**
 * Hides the navbar when the user scrolls down and reveals it again as soon as
 * they start scrolling up. Auto-hide only kicks in on pages with a header
 * banner, and only once the user has scrolled past it — pages without a banner
 * (e.g. search) keep the navbar visible by default. Returns whether the navbar
 * should be hidden.
 */
export function useHideOnScroll(t0) {
  const $ = _c(13);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    bannerSelector: t2,
    minBannerHeight: t3,
    threshold: t4,
    disabled: t5
  } = t1;
  const bannerSelector = t2 === undefined ? "[data-page-header-banner]" : t2;
  const minBannerHeight = t3 === undefined ? 120 : t3;
  const threshold = t4 === undefined ? 5 : t4;
  const disabled = t5 === undefined ? false : t5;
  const [hidden, setHidden] = useState(false);
  const pathname = usePathname();
  let t6;
  if ($[2] !== bannerSelector || $[3] !== disabled || $[4] !== minBannerHeight || $[5] !== threshold) {
    t6 = () => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (disabled || reduceMotion) {
        const id = window.requestAnimationFrame(() => setHidden(false));
        return () => window.cancelAnimationFrame(id);
      }
      const banner = document.querySelector(bannerSelector);
      let revealOffset = banner?.offsetHeight ?? 0;
      const measure = () => {
        revealOffset = banner?.offsetHeight ?? 0;
      };
      let lastScrollY = window.scrollY;
      let ticking = false;
      const update = () => {
        const currentY = window.scrollY;
        if (revealOffset < minBannerHeight) {
          setHidden(false);
          lastScrollY = currentY;
          ticking = false;
          return;
        }
        const delta = currentY - lastScrollY;
        if (Math.abs(delta) > threshold) {
          if (delta > 0 && currentY > revealOffset) {
            setHidden(true);
          } else {
            if (delta < 0) {
              setHidden(false);
            }
          }
          lastScrollY = currentY;
        }
        ticking = false;
      };
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      const initialId = window.requestAnimationFrame(() => setHidden(false));
      const observer = banner ? new ResizeObserver(measure) : null;
      if (banner && observer) {
        observer.observe(banner);
      }
      return () => {
        window.removeEventListener("scroll", onScroll);
        window.cancelAnimationFrame(initialId);
        observer?.disconnect();
      };
    };
    $[2] = bannerSelector;
    $[3] = disabled;
    $[4] = minBannerHeight;
    $[5] = threshold;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== bannerSelector || $[8] !== disabled || $[9] !== minBannerHeight || $[10] !== pathname || $[11] !== threshold) {
    t7 = [bannerSelector, minBannerHeight, threshold, disabled, pathname];
    $[7] = bannerSelector;
    $[8] = disabled;
    $[9] = minBannerHeight;
    $[10] = pathname;
    $[11] = threshold;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  useEffect(t6, t7);
  return disabled ? false : hidden;
}